/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: emergencyNumberApi.ts
 🗓️ Created: 2025-12-04
 🧩 Description:
    API client for Emergency Numbers
────────────────────────────────────────────────────────────────*/

import { api } from '../../../lib/api';
import type {
  EmergencyNumber,
  EmergencyNumberFormState,
  ListEmergencyNumbersQuery,
  ListEmergencyNumbersResponse,
} from '../types';

const BASE_URL = '/api/telephony/emergency-numbers';

/**
 * Fetch emergency numbers list
 */
export async function fetchEmergencyNumbers(
  query: ListEmergencyNumbersQuery,
  search?: string,
  domainId?: number
): Promise<ListEmergencyNumbersResponse> {
  const params = new URLSearchParams();

  if (query.page) params.append('page', query.page.toString());
  if (query.limit) params.append('limit', query.limit.toString());
  if (query.sortBy) params.append('sortBy', query.sortBy);
  if (query.sortOrder) params.append('sortOrder', query.sortOrder);
  if (search) params.append('search', search);
  if (query.enabled != null) params.append('enabled', query.enabled.toString());
  if (query.countryCode) params.append('countryCode', query.countryCode);
  if (domainId != null) params.append('domainId', domainId.toString());

  const response = await api.get(`${BASE_URL}?${params.toString()}`);
  return response.data;
}

/**
 * Fetch emergency number by ID
 */
export async function fetchEmergencyNumberById(id: string): Promise<EmergencyNumber> {
  const response = await api.get(`${BASE_URL}/${id}`);
  return response.data.data;
}

/**
 * Create emergency number
 */
export async function createEmergencyNumber(
  data: EmergencyNumberFormState
): Promise<EmergencyNumber> {
  const response = await api.post(BASE_URL, data);
  return response.data.data;
}

/**
 * Update emergency number
 */
export async function updateEmergencyNumber(
  id: string,
  data: Partial<EmergencyNumberFormState>
): Promise<EmergencyNumber> {
  const response = await api.put(`${BASE_URL}/${id}`, data);
  return response.data.data;
}

/**
 * Delete emergency number
 */
export async function deleteEmergencyNumber(id: string): Promise<void> {
  await api.delete(`${BASE_URL}/${id}`);
}

/**
 * Get route types
 */
export async function fetchRouteTypes(): Promise<{ value: string; label: string }[]> {
  const response = await api.get(`${BASE_URL}/meta/route-types`);
  return response.data.data;
}

